import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import router from '@/router';
import type {
  Skill,
  JobResultSkill,
  UserSkill,
  UserJob,
  JobResult,
  ProcessStep,
  JobSearch,
  Location,
  SubscriptionPlan,
  UserSubscription,
  BillingTransaction,
  Country,
  StateProvince,
  SupportTicket,
  SupportReply,
} from '@/types';

const API_URL = process.env.VUE_APP_API_URL || 'http://optyfind-backend.localhost';

const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach Bearer token from Pinia auth store
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — clear auth state and redirect on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      await useAuthStore().logout();
      router.push('/login');
    }
    return Promise.reject(error);
  }
);

// Fetch all job applications
export const fetchJobs = (): Promise<AxiosResponse<{ jobs: UserJob[] }>> => apiClient.get("/jobs");

export const fetchSkills = (): Promise<AxiosResponse<Skill[]>> => apiClient.get("/skills");

export const fetchUserSkills = (): Promise<AxiosResponse<UserSkill[]>> => apiClient.get("/user-skills");

export const addSkill = (skill_name: string, is_mine: boolean): Promise<AxiosResponse<{ skill_id: number; skill_name: string }>> =>
  apiClient.post("/skills", { skill_name, is_mine });

export const addMySkill = (
  skillId: number,
  yearsExperience?: number,
  jobId?: number | string
): Promise<AxiosResponse<{
  user_skill_id: number;
  skill_id: number;
  skill_name: string;
  years_experience: number | null;
  message: string;
  job_skill: JobResultSkill | null;
}>> => apiClient.post(`/user-skills`, {
  skill_id: skillId,
  ...(yearsExperience !== undefined ? { years_experience: yearsExperience } : {}),
  ...(jobId !== undefined ? { job_id: jobId } : {}),
});

export const removeSkillFromMySkills = (skillId: number): Promise<AxiosResponse<void>> =>
  apiClient.delete(`/user-skills/${skillId}`);

export const updateMySkillYears = (skillId: number, yearsExperience: number): Promise<AxiosResponse<void>> =>
  apiClient.put(`/user-skills/${skillId}`, { years_experience: yearsExperience });

export const searchLocations = (query: string): Promise<AxiosResponse<{ suggestions: Location[] }>> =>
  apiClient.get(`/locations/search`, { params: { q: query } });

// Add a new job application
export const addJob = (jobData: Partial<UserJob>): Promise<AxiosResponse<UserJob>> =>
  apiClient.post("/jobs", jobData);

export const updateJob = (jobId: number | string, jobData: Partial<UserJob>): Promise<AxiosResponse<UserJob>> =>
  apiClient.put(`/job/${jobId}`, jobData);

// Fetch a single job application with details
export const fetchJobDetails = (jobId: number | string): Promise<AxiosResponse<UserJob>> =>
  apiClient.get(`/jobs/view/${jobId}`);

// Fetch predefined statuses
export const fetchStatuses = (): Promise<AxiosResponse<{ statuses: string[] }>> =>
  apiClient.get("/statuses");

export const fetchSearchResults = (searchId: number | string): Promise<AxiosResponse<{
  jobs: JobResult[];
  search_title: string;
  search_id: number;
  total_jobs: number;
}>> => apiClient.get(`/job-results/${searchId}`);

export const fetchFoundJobs = (): Promise<AxiosResponse<{ jobs: JobResult[] }>> =>
  apiClient.get('/job-results');

// Update job status
export const updateJobStatus = (
  jobId: number | string,
  status: string,
  status_date?: string,
  status_time?: string,
  notes?: string
): Promise<AxiosResponse<ProcessStep>> =>
  apiClient.post(`/application_process`, {
    job_id: jobId,
    process_type: status,
    process_date: status_date,
    process_time: status_time || undefined,
    notes: notes || undefined,
  });

export const createJobSearch = async (searchData: Record<string, unknown>): Promise<unknown> => {
  const response = await apiClient.post('/job-searches', searchData);
  return response.data;
};

export const fetchJobSearches = (): Promise<AxiosResponse<{ searches: JobSearch[] }>> =>
  apiClient.get('/job-searches');

export const getJobSearch = (searchId: number | string): Promise<AxiosResponse<JobSearch>> =>
  apiClient.get(`/job-searches/${searchId}`);

export const updateJobSearch = (searchId: number | string, data: Record<string, unknown>): Promise<AxiosResponse<JobSearch>> =>
  apiClient.put(`/job-searches/${searchId}`, data);

export const deleteJobSearch = (searchId: number): Promise<AxiosResponse<void>> =>
  apiClient.delete(`/job-searches/${searchId}`);

// Remove a process step
export const removeProcessStep = (stepId: number): Promise<AxiosResponse<void>> =>
  apiClient.delete(`/application_process/${stepId}`);

export const updateProcessStep = (
  stepId: number,
  data: Partial<ProcessStep>
): Promise<AxiosResponse<ProcessStep>> =>
  apiClient.put(`/application_process/${stepId}`, data);

export const addSkillToJobSearch = (searchId: number | string, skillId: number, minYears?: number): Promise<AxiosResponse<void>> =>
  apiClient.post(`/job-searches/${searchId}/skills`, { skill_id: skillId, ...(minYears !== undefined ? { min_years: minYears } : {}) });

export const numberOfJobsWithInterview = (): Promise<AxiosResponse<{ interview_job_count: number }>> =>
  apiClient.get("/jobs/with-interviews");

// User / profile
export const updateUser = (data: Record<string, string>): Promise<AxiosResponse<{ user: import('@/types').User }>> =>
  apiClient.put('/auth/user', data);

// Plans & subscriptions
export const fetchPlans = (): Promise<AxiosResponse<{ plans: SubscriptionPlan[] }>> =>
  apiClient.get('/plans');
export const fetchSubscription = (): Promise<AxiosResponse<{ subscription: UserSubscription }>> =>
  apiClient.get('/user/subscription');
export const updateSubscription = (data: { plan_id: number; billing_period: string }): Promise<AxiosResponse<void>> =>
  apiClient.post('/user/subscription', data);
export const downgradePlan = (data: { plan_id: number; billing_period: string }): Promise<AxiosResponse<{ subscription: UserSubscription }>> =>
  apiClient.post('/billing/change-plan', data);

// Billing
export const getBillingCheckoutToken = (
  plan_id: number,
  billing_period: string,
  coupon: string | null = null
): Promise<AxiosResponse<{
  publishable_key: string;
  client_secret: string | null;
  stripe_subscription_id: string;
  requires_payment: boolean;
}>> => apiClient.post('/billing/checkout-token', {
  plan_id,
  billing_period,
  ...(coupon ? { coupon } : {}),
});

export const confirmBillingPayment = (data: {
  stripe_subscription_id: string;
}): Promise<AxiosResponse<void>> => apiClient.post('/billing/confirm', data);

export const fetchBillingTransactions = (): Promise<AxiosResponse<{ transactions: BillingTransaction[] }>> =>
  apiClient.get('/billing/transactions');

export const validateCoupon = (code: string): Promise<AxiosResponse<{ discount_percent: number }>> =>
  apiClient.get('/billing/validate-coupon', { params: { code } });

export const fetchCountries = (): Promise<AxiosResponse<{ countries: Country[] }>> =>
  apiClient.get('/geo/countries');

export const fetchStates = (country: string): Promise<AxiosResponse<{ states: StateProvince[] }>> =>
  apiClient.get('/geo/states', { params: { country } });

// Support
export const fetchSupportTickets = (): Promise<AxiosResponse<{ tickets: SupportTicket[] }>> =>
  apiClient.get('/user/support');

export const createSupportTicket = (subject: string, message: string): Promise<AxiosResponse<{ ticket: SupportTicket }>> =>
  apiClient.post('/user/support', { subject, message });

export const fetchSupportTicket = (id: number): Promise<AxiosResponse<{ ticket: SupportTicket }>> =>
  apiClient.get(`/user/support/${id}`);

export const replySupportTicket = (id: number, message: string): Promise<AxiosResponse<{ reply: SupportReply }>> =>
  apiClient.post(`/user/support/${id}`, { message });

export const closeSupportTicket = (id: number): Promise<AxiosResponse<{ message: string }>> =>
  apiClient.put(`/user/support/${id}`);

export { apiClient };

export default {
  fetchJobs,
  fetchSkills,
  fetchUserSkills,
  addJob,
  addSkill,
  addMySkill,
  removeSkillFromMySkills,
  updateMySkillYears,
  searchLocations,
  fetchJobDetails,
  fetchStatuses,
  updateJobStatus,
  updateJob,
  removeProcessStep,
  updateProcessStep,
  numberOfJobsWithInterview,
  fetchSearchResults,
  fetchFoundJobs,
  createJobSearch,
  fetchJobSearches,
  getJobSearch,
  updateJobSearch,
  deleteJobSearch,
  addSkillToJobSearch,
  fetchPlans,
  fetchSubscription,
  updateSubscription,
  downgradePlan,
  getBillingCheckoutToken,
  confirmBillingPayment,
  fetchBillingTransactions,
  validateCoupon,
  fetchCountries,
  fetchStates,
};
